/* The Skills page's writes. Each screen draws its own failure — problems pinned to a
   file, a refusal that lists agents — so every one is silent, and every screen that
   uses one renders `error` in place (rule book §3, "Nothing fails silently"). A saved
   skill changes the catalog and an agent's skills, so a write re-reads all of `skills`. */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { skillsKeys } from "@/shared/api/skills";

import { checkSkill, createSkill, deleteSkill, importFromGithub, updateSkill } from "../api/write";

function useSkillWrite<A, T>(write: (args: A) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => qc.invalidateQueries({ queryKey: skillsKeys.all }),
    meta: { silent: true },
  });
}

/** Stores nothing, so it invalidates nothing. */
export const useCheckSkill = () => useMutation({ mutationFn: checkSkill, meta: { silent: true } });
export const useCreateSkill = () => useSkillWrite(createSkill);
export const useUpdateSkill = () => useSkillWrite(({ id, form }: { id: string; form: FormData }) => updateSkill(id, form));
/** The deleted skill's own queries are left alone: refetched, they could only 404 (and the
    page that holds them is about to close). */
export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSkill,
    onSuccess: (_, id) => qc.invalidateQueries({ queryKey: skillsKeys.all, predicate: (q) => !q.queryKey.includes(id) }),
    meta: { silent: true },
  });
}
export const useImportGithub = () => useSkillWrite(({ url, folder }: { url: string; folder?: string }) => importFromGithub(url, folder));
